import dayjs from 'dayjs';
import pluralize from 'pluralize';

import { Box, Typography } from '@cast/design-system';
import type { BillingV1FeatureUsageSummary } from '@cast/types';

import { usePlatformUsageDetailQuery } from 'hooks/queries/organization';

import { OverviewCard } from '../../_components/OverviewCard';
import { DATABASE_OPTIMIZATION_FEATURE_KEY } from '../constants';

type DatabaseOptimizationOverviewCardProps = {
  feature: BillingV1FeatureUsageSummary;
  onClick: () => void;
};

export const DatabaseOptimizationOverviewCard = ({
  feature,
  onClick,
}: DatabaseOptimizationOverviewCardProps) => {
  const periodFrom = dayjs().startOf('month').format('YYYY-MM-DD');
  const periodTo = dayjs().endOf('month').format('YYYY-MM-DD');

  const { data } = usePlatformUsageDetailQuery({
    feature: DATABASE_OPTIMIZATION_FEATURE_KEY,
    periodFrom,
    periodTo,
  });

  const instanceCount = data?.detail?.entities?.length;

  const freeOfCharge = feature.freeOfCharge;
  const credits = feature.credits;

  return (
    <Box data-testid="dbo-overview-card">
      <OverviewCard
        testId="platform-usage-overview-card"
        title={feature.displayName ?? 'Database Optimization'}
        badge={
          freeOfCharge?.isFreeOfCharge
            ? {
                label: 'Free of charge',
                endDate: freeOfCharge.endDate,
              }
            : undefined
        }
        usage={{
          value: feature.usage ?? 0,
          unit: undefined,
          subline:
            instanceCount !== undefined ? (
              <Typography variant="P12R" data-testid="dbo-overview-subline">
                across {instanceCount}{' '}
                {pluralize('database instance', instanceCount)}
              </Typography>
            ) : undefined,
          infoTooltip:
            'Calculated as database instances normalized over the total hours of the current billing month. Usage builds progressively throughout the month.',
        }}
        credits={
          credits
            ? {
                used: credits.used ?? 0,
                total: credits.total ?? 0,
                remaining: credits.remaining ?? 0,
                period: credits.resetFrequency,
                periodTooltip:
                  credits.resetFrequency === 'monthly'
                    ? 'Your credit allocation renews on the 1st of each month.'
                    : undefined,
              }
            : undefined
        }
        onClick={onClick}
      />
    </Box>
  );
};
